import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useMutation } from '@grandvisionhq/graphql';
import { useCartStorage } from '@grandvisionhq/lib-utils-cart';
import { useDispatchTrackErrorEvent } from '@grandvisionhq/svc-tealium/events';
import { errorsMapping } from '../../mappers/errors-mapping.js';
import '../../operations/mutations/set-customer-details.js';
import '../../operations/mutations/set-service-store.js';
import { SET_SHIPPING_METHOD } from '../../operations/mutations/set-shipping-method.js';
import '../../operations/mutations/set-shipping-state.js';
import '../../operations/mutations/create-apple-pay-session.js';
import '../../operations/mutations/recreate-ordered-checkout.js';
import 'lodash/isEmpty';
import { getFlattenedCartFailureErrorCodes } from '../../utils/get-flattened-cart-error-codes.js';
import '../../utils/get-customer-details-payload.js';
import '../../models/cart/cart-state.js';
import '../../models/checkout/checkout-step-error-type.js';
import '../../models/checkout-settings.js';
import '../../models/step-shipping/shipping-methods.js';
import '../../models/shared.js';
import 'history';
import '@grandvisionhq/utils';
import '../../utils/has-cart-errors.js';
import { useMutationConfig } from './use-mutation-config.js';
import { useUpdateCartCache } from './use-update-cart-cache.js';

const useSetShippingMethod = ({ onError , onCompleted  } = {})=>{
    const [{ cartId , cartHash  }] = useCartStorage();
    const { updateCacheWithCart  } = useUpdateCartCache();
    const [selectedShippingMethod, setSelectedShippingMethod] = React.useState(undefined);
    const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
    /**
   * Handle success and error response from mutation operation
   * @param responseData
   */ const handleOnCompleted = React.useCallback((responseData)=>{
        let result;
        if ('setShippingMethod' in responseData) {
            var ref;
            result = responseData.setShippingMethod;
            if ((ref = result.failures) === null || ref === void 0 ? void 0 : ref.length) {
                const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
                onError === null || onError === void 0 ? void 0 : onError({
                    reason: 'MUTATION'
                });
                /**
           *  Notify user about all failures
           *  To handle granular errors return from mutation operation
           */ errorCodes.forEach((error)=>toast.failed(error)
                );
            }
            if (result.success) {
                selectedShippingMethod && (onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('MUTATION', selectedShippingMethod));
            }
        }
    }, [
        onCompleted,
        onError,
        selectedShippingMethod
    ]);
    const handleOnError = React.useCallback(()=>{
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'MUTATION'
        });
        dispatchTrackErrorEvent(()=>Promise.resolve({
                Error_Source: 'Server',
                Error_Code: 'Set shipping method error',
                Error_Details: 'Mutation failed',
                Error_Message: errorsMapping.SOMETHING_WENT_WRONG
            })
        );
        // General error for apollo error response
        toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
    }, [
        dispatchTrackErrorEvent,
        onError
    ]);
    const updateCache = React.useCallback((cache, { data  })=>{
        if (data && data.setShippingMethod.success) {
            const updatedCart = data.setShippingMethod.cart;
            updatedCart && updateCacheWithCart({
                cart: updatedCart,
                cache
            });
        }
    }, [
        updateCacheWithCart
    ]);
    const { mutationOptions  } = useMutationConfig({
        onError: handleOnError,
        onCompleted: handleOnCompleted
    });
    const [mutationCallback, mutation] = useMutation(SET_SHIPPING_METHOD, swcHelpers.objectSpread({}, mutationOptions, {
        // reason: typescript complains about a typing mismatch for the data prop
        update: updateCache,
        context: {
            forwardHeaders: {
                'cart-hash': cartHash
            }
        }
    }));
    /**
   *  Shipping method data update operation
   *  @param: shippingMethodId(UUID)
   */ const setShippingMethod = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(shippingMethodId, globalStoreId) {
            yield mutationCallback({
                variables: {
                    shippingMethodInput: {
                        shippingMethodId,
                        checkoutId: cartId,
                        globalStoreId
                    }
                }
            });
        });
        return function(shippingMethodId, globalStoreId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        mutationCallback,
        cartId
    ]);
    return {
        setShippingMethod,
        isUpdating: mutation.loading,
        updateError: mutation.error,
        setSelectedShippingMethod
    };
};

export { useSetShippingMethod };
//# sourceMappingURL=use-set-shipping-method.js.map
